import {
  ConversationContext,
  ConversationEventType,
  ConversationMessage,
  ConversationMessageStatus,
  ConversationParticipant,
  ConversationStatus,
  SelectOptionAction,
  StartConversationParams,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessage,
  ConversationsApiDataMessagePart,
} from '../generated/types';
import type {ReportWarning} from './ApiMethods';
import {FrameworkAdapter} from './FrameworkAdapter';
import {emitChannelTransitionContext} from './ChannelTransition';
import {emitInputModeContextValues} from './EmitInputModeContext';

type DeleteConversationResponse =
  | {type: 'success'; data: undefined}
  | {type: 'error'; error: string; code: number};

type EndedConversationDeleteState = {
  completedConversationIds: Set<string>;
  pendingConversationIds: Set<string>;
};

const endedConversationDeleteStates = new WeakMap<
  (event: ConversationEventType) => void,
  EndedConversationDeleteState
>();

type OptionsPromptMessage = Extract<
  ConversationMessage,
  | {type: 'button-options-conversation-message'}
  | {type: 'select-options-conversation-message'}
>;

type OptionsPromptLike = OptionsPromptMessage | SelectOptionAction['message'];

export function buildDeltaResponseState({
  conversationId,
  apiMessages,
  participantsById,
  user,
  includeUserMessages = false,
  reportWarning,
  attachmentDownloadPath,
}: {
  conversationId: string;
  apiMessages: ConversationsApiDataMessage[];
  participantsById: Record<string, ConversationParticipant>;
  user?: NonNullable<StartConversationParams['participant']>;
  includeUserMessages?: boolean;
  reportWarning?: ReportWarning;
  attachmentDownloadPath?: string;
}): {
  context: Partial<ConversationContext> & {enabledInputModes?: string[]};
  ended: boolean;
  messages: ConversationMessage[];
} {
  return {
    context: FrameworkAdapter.buildDeltaContextFromApiMessages(apiMessages),
    ended: FrameworkAdapter.hasBeginCsatTermination(apiMessages),
    messages: FrameworkAdapter.visibleMessagesFromApiMessages({
      conversationId,
      apiMessages,
      participantsById,
      user,
      includeUserMessages,
      reportWarning,
      attachmentDownloadPath,
    }),
  };
}

export function emitDeltaResponseContext(
  emitConversationEvent: (event: ConversationEventType) => void,
  conversationId: string,
  context: Partial<ConversationContext> & {enabledInputModes?: string[]},
): void {
  emitChannelTransitionContext(emitConversationEvent, conversationId, context);
  emitInputModeContextValues(
    emitConversationEvent,
    conversationId,
    context.enabledInputModes,
  );
}

export function emitAddedMessages(
  emitConversationEvent: (event: ConversationEventType) => void,
  conversationId: string,
  messages: ConversationMessage[],
): void {
  messages.forEach((message) => {
    emitConversationEvent({
      name: 'conversation-message-added',
      conversationId,
      participantId: message.participant.id,
      messageId: message.id,
      nonce: message.nonce,
      message,
    });
  });
}

export async function refetchConversationAndEmitDelta({
  getConversation,
  emitConversationEvent,
  getCachedMessage,
  conversationId,
  participantsById,
  user,
  attachmentDownloadPath,
  deleteConversation,
  reportWarning,
}: {
  getConversation?: (
    targetConversationId: string,
  ) => Promise<
    | {type: 'success'; data: ConversationsApiDataConversation}
    | {type: 'error'; error: string; code: number}
  >;
  emitConversationEvent: (event: ConversationEventType) => void;
  getCachedMessage?: (
    targetConversationId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
  conversationId: string;
  participantsById: Record<string, ConversationParticipant>;
  user?: NonNullable<StartConversationParams['participant']>;
  attachmentDownloadPath?: string;
  deleteConversation?: (id: string) => Promise<DeleteConversationResponse>;
  reportWarning?: ReportWarning;
}): Promise<void> {
  if (!getConversation) {
    return;
  }

  const response = await getConversation(conversationId);
  if (response.type === 'error') {
    return;
  }

  const {context, ended, messages} = buildDeltaResponseState({
    conversationId,
    apiMessages: response.data.messages,
    participantsById,
    user,
    attachmentDownloadPath,
  });

  await emitDeltaResponse({
    emitConversationEvent,
    conversationId,
    context,
    ended,
    deleteConversation,
    reportWarning,
    typingEndedParticipantId: user?.id,
    emitMessages: () =>
      emitReconciledMessages({
        emitConversationEvent,
        conversationId,
        getCachedMessage,
        messages,
      }),
  });
}

export function emitUpdatedMessage(
  emitConversationEvent: (event: ConversationEventType) => void,
  conversationId: string,
  message: ConversationMessage,
): void {
  emitConversationEvent({
    name: 'conversation-message-updated',
    conversationId,
    participantId: message.participant.id,
    messageId: message.id,
    nonce: message.nonce,
    message,
  });
}

export function selectedOptionIdFromSubmittedMessage(
  message: ConversationMessage,
): string | undefined {
  if (
    message.type === 'attachment-conversation-message' ||
    message.type === 'text-with-attachments-conversation-message'
  ) {
    return 'text-with-attachments-input';
  }

  if (message.type === 'text-conversation-message' && message.text !== '') {
    return 'text-input';
  }

  return undefined;
}

export function selectedOptionIdFromSubmissionParts(
  parts: ConversationsApiDataMessagePart[],
): string | undefined {
  const selectedOptionPart = parts.find(
    (part) => part.type === 'selected_option',
  );
  if (selectedOptionPart?.type === 'selected_option') {
    return selectedOptionPart.content.id;
  }

  if (parts.some((part) => part.type === 'attachment')) {
    return 'text-with-attachments-input';
  }

  if (parts.some((part) => part.type === 'text')) {
    return 'text-input';
  }

  return undefined;
}

export function buildAnsweredOptionsPromptMessage({
  message,
  participant,
  selectedOptionId,
  timestamp = Date.now(),
}: {
  message?: OptionsPromptLike;
  participant: ConversationParticipant;
  selectedOptionId?: string;
  timestamp?: number;
}): ConversationMessage | undefined {
  if (!selectedOptionId || !isOptionsPromptMessage(message)) {
    return undefined;
  }

  return {
    ...message,
    participant,
    selectedOptionId,
    status: ConversationMessageStatus.Sent,
    timestamp: (message as {timestamp?: number}).timestamp ?? timestamp,
  } as ConversationMessage;
}

export function buildCachedAnsweredOptionsPromptMessage({
  conversationId,
  creatorPartId,
  getCachedMessage,
  participant,
  selectedOptionId,
  fallbackMessage,
}: {
  conversationId: string;
  creatorPartId?: string;
  getCachedMessage?: (
    convId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
  participant: ConversationParticipant;
  selectedOptionId?: string;
  fallbackMessage?: OptionsPromptLike;
}): ConversationMessage | undefined {
  const cachedMessage =
    creatorPartId && getCachedMessage
      ? (getCachedMessage(conversationId, creatorPartId) as
          | OptionsPromptLike
          | undefined)
      : undefined;

  return buildAnsweredOptionsPromptMessage({
    message: cachedMessage ?? fallbackMessage,
    participant,
    selectedOptionId,
  });
}

function endedConversationDeleteStateFor(
  emitConversationEvent: (event: ConversationEventType) => void,
): EndedConversationDeleteState {
  const existingState = endedConversationDeleteStates.get(
    emitConversationEvent,
  );
  if (existingState) {
    return existingState;
  }

  const nextState = {
    completedConversationIds: new Set<string>(),
    pendingConversationIds: new Set<string>(),
  };
  endedConversationDeleteStates.set(emitConversationEvent, nextState);
  return nextState;
}

function reportEndedConversationDeleteError({
  conversationId,
  error,
  code,
  reportWarning,
}: {
  conversationId: string;
  error: string;
  code?: number;
  reportWarning?: ReportWarning;
}): void {
  reportWarning?.('error deleting conversation after begin_csat termination', {
    tags: {
      conversation_id: conversationId,
      ...(code === undefined ? {} : {code}),
      error,
    },
  });
}

function deleteEndedConversation({
  emitConversationEvent,
  conversationId,
  deleteConversation,
  reportWarning,
}: {
  emitConversationEvent: (event: ConversationEventType) => void;
  conversationId: string;
  deleteConversation?: (id: string) => Promise<DeleteConversationResponse>;
  reportWarning?: ReportWarning;
}): void {
  if (!deleteConversation) {
    return;
  }

  const deleteState = endedConversationDeleteStateFor(emitConversationEvent);
  if (
    deleteState.completedConversationIds.has(conversationId) ||
    deleteState.pendingConversationIds.has(conversationId)
  ) {
    return;
  }

  deleteState.pendingConversationIds.add(conversationId);

  deleteConversation(conversationId)
    .then((response) => {
      deleteState.pendingConversationIds.delete(conversationId);

      if (response.type === 'success' || response.code === 404) {
        deleteState.completedConversationIds.add(conversationId);
        return;
      }

      reportEndedConversationDeleteError({
        conversationId,
        error: response.error,
        code: response.code,
        reportWarning,
      });
    })
    .catch((error: unknown) => {
      deleteState.pendingConversationIds.delete(conversationId);
      reportEndedConversationDeleteError({
        conversationId,
        error: error instanceof Error ? error.message : 'Unknown error',
        reportWarning,
      });
    });
}

export async function emitDeltaResponse({
  emitConversationEvent,
  conversationId: convId,
  promptUpdate,
  context,
  ended,
  emitMessages,
  deleteConversation,
  typingEndedParticipantId,
  reportWarning,
}: {
  emitConversationEvent: (event: ConversationEventType) => void;
  conversationId: string;
  promptUpdate?: ConversationMessage;
  context: Partial<ConversationContext> & {enabledInputModes?: string[]};
  ended: boolean;
  emitMessages: () => void | Promise<void>;
  deleteConversation?: (id: string) => Promise<DeleteConversationResponse>;
  typingEndedParticipantId?: string;
  reportWarning?: ReportWarning;
}): Promise<void> {
  // v1 parity (universal-help client.ts `handleEffects`): emit the
  // answered-prompt update and all new messages BEFORE the context. A channel
  // transition carried in the context synchronously triggers the chat handoff,
  // which disconnects this conversation's client listener; any message events
  // emitted afterward are dropped and only reappear after a refetch.
  if (promptUpdate) {
    emitUpdatedMessage(emitConversationEvent, convId, promptUpdate);
  }
  await emitMessages();
  if (ended) {
    if (typingEndedParticipantId) {
      emitConversationEvent({
        name: 'conversation-typing-ended',
        conversationId: convId,
        participantId: typingEndedParticipantId,
      });
    }
    emitConversationEvent({
      name: 'conversation-updated',
      conversationId: convId,
      status: ConversationStatus.Inactive,
    });
  }
  emitDeltaResponseContext(emitConversationEvent, convId, context);
  if (ended) {
    deleteEndedConversation({
      emitConversationEvent,
      conversationId: convId,
      deleteConversation,
      reportWarning,
    });
  }
}

function emitReconciledMessages({
  emitConversationEvent,
  conversationId,
  getCachedMessage,
  messages,
}: {
  emitConversationEvent: (event: ConversationEventType) => void;
  conversationId: string;
  getCachedMessage?: (
    targetConversationId: string,
    messageId: string,
  ) => ConversationMessage | undefined;
  messages: ConversationMessage[];
}): void {
  messages.forEach((message) => {
    const cachedMessage = getCachedMessage?.(conversationId, message.id);
    emitConversationEvent({
      name: cachedMessage
        ? 'conversation-message-updated'
        : 'conversation-message-added',
      conversationId,
      participantId: message.participant.id,
      messageId: message.id,
      nonce: message.nonce,
      message,
    });
  });
}

function isOptionsPromptMessage(
  message?: OptionsPromptLike,
): message is OptionsPromptLike {
  return (
    message?.type === 'button-options-conversation-message' ||
    message?.type === 'select-options-conversation-message'
  );
}
